/**
 * Theme Configuration
 *
 * This file defines all available themes and their metadata.
 * Theme CSS files are located in /public/themes/ directory.
 *
 * NOTE: CSS files in public/ are NOT processed by Vite,
 * so their paths remain stable in both development and production.
 */

export interface ThemeConfig {
  /** Unique identifier for the theme */
  id: string
  /** Display name shown in UI */
  name: string
  /** Brief description of the theme */
  description?: string
  /** CSS filename (without path, files are in /public/themes/) */
  cssFile: string
  /** Preview image or color palette (optional) */
  preview?: {
    primary: string
    background: string
    foreground: string
  }
}

/**
 * Base path for theme CSS files
 * Files are served from /public/themes/ which maps to /themes/ in the browser
 */
export const THEME_CSS_BASE_PATH = '/themes'

/**
 * List of all available themes
 *
 * 📝 How to Add a New Theme:
 * 1. Create a new CSS file in `public/themes/` (e.g., `my-theme.css`)
 * 2. Add theme configuration here following the structure below
 * 3. The CSS file will be dynamically loaded when the theme is selected
 */
export const themes: ThemeConfig[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Default theme',
    cssFile: 'default.css',
    preview: {
      primary: '#e05d38',
      background: '#e8ebed',
      foreground: '#333333',
    },
  },
  {
    id: 'claude',
    name: 'Claude',
    description: 'Clean and modern theme inspired by Claude AI',
    cssFile: 'claude.css',
    preview: {
      primary: 'oklch(0.6171 0.1375 39.0427)',
      background: 'oklch(0.9818 0.0054 95.0986)',
      foreground: 'oklch(0.3438 0.0269 95.7226)',
    },
  },
  {
    id: 'catppucin',
    name: 'Catppuccin',
    description: 'Soothing pastel theme',
    cssFile: 'catppucin.css',
    preview: {
      primary: 'oklch(0.5547 0.2503 297.0156)',
      background: 'oklch(0.9578 0.0058 264.5321)',
      foreground: 'oklch(0.4355 0.043 279.325)',
    },
  },
  {
    id: 'caffeine',
    name: 'Caffeine',
    description: 'High contrast energetic theme',
    cssFile: 'caffeine.css',
    preview: {
      primary: 'oklch(0.4341 0.0392 41.9938)',
      background: 'oklch(0.9821 0 0)',
      foreground: 'oklch(0.2435 0 0)',
    },
  },
  {
    id: 'amber-minimal',
    name: 'Amber Minimal',
    description: 'Minimalist amber-toned theme',
    cssFile: 'amber-minimal.css',
    preview: {
      primary: 'oklch(0.7686 0.1647 70.0804)',
      background: 'oklch(1 0 0)',
      foreground: 'oklch(0.2686 0 0)',
    },
  },
  {
    id: 'amethyst-haze',
    name: 'Amethyst Haze',
    description: 'Purple-tinted dreamy theme',
    cssFile: 'amethyst-haze.css',
    preview: {
      primary: 'oklch(0.6104 0.0767 299.7335)',
      background: 'oklch(0.9777 0.0041 301.4256)',
      foreground: 'oklch(0.3651 0.0325 287.0807)',
    },
  },
  {
    id: 'bold-tech',
    name: 'Bold Tech',
    description: 'Bold and technical theme',
    cssFile: 'bold-tech.css',
    preview: {
      primary: 'oklch(0.6056 0.2189 292.7172)',
      background: 'oklch(1 0 0)',
      foreground: 'oklch(0.3588 0.1354 278.6973)',
    },
  },
  {
    id: 'bubblegum',
    name: 'Bubblegum',
    description: 'Playful and colorful theme',
    cssFile: 'bubblegum.css',
    preview: {
      primary: 'oklch(0.6209 0.1801 348.1385)',
      background: 'oklch(0.9399 0.0203 345.6985)',
      foreground: 'oklch(0.4712 0 0)',
    },
  },
  {
    id: 'candyland',
    name: 'Candyland',
    description: 'Sweet and vibrant theme',
    cssFile: 'candyland.css',
    preview: {
      primary: 'oklch(0.8677 0.0735 7.0855)',
      background: 'oklch(0.9809 0.0025 228.7836)',
      foreground: 'oklch(0.3211 0 0)',
    },
  },
] as const

/**
 * Type-safe theme IDs
 */
export type ThemeId = (typeof themes)[number]['id']

/**
 * Default theme ID
 */
export const DEFAULT_THEME_ID: ThemeId = 'default'

/**
 * Get theme configuration by ID
 */
export function getThemeById(id: string): ThemeConfig | undefined {
  return themes.find((theme) => theme.id === id)
}

/**
 * Get the full CSS path for a theme
 */
export function getThemeCssPath(theme: ThemeConfig): string {
  return `${THEME_CSS_BASE_PATH}/${theme.cssFile}`
}

/**
 * Get all theme IDs
 */
export function getThemeIds(): ThemeId[] {
  return themes.map((theme) => theme.id)
}
